// ВОЛНА W: канал «мутация → сцена» для премиальных эффектов посадки.
//
// Мутации (копия/вставка/зеркало/undo/redo/фасадная схема) живут в shared/api и
// не знают о сцене; сцена не знает о мутациях. Тот же мост, что у синхронизации
// команд (`MODULE_COMMAND_COMMITTED_EVENT` в module-state-sync): window-событие
// с деталью, подписка возвращает отписку. Сцена фильтрует события по modelId —
// вставка в фоновую модель не анимируется в чужом канвасе.

import type { AppearOptions } from './placementEffects';

export const PLACEMENT_EFFECT_EVENT = 'holzcom-placement-effect';

export type PlacementEffectRequest =
  | ({ kind: 'appear'; entityIds: string[] } & AppearOptions)
  /** Undo/redo: вернувшиеся сущности появляются мягко, без вспышки. Идентификаторы
   *  неизвестны до перечитывания бандла — сцена сама диффит набор модулей. */
  | { kind: 'appear-returning' }
  /** bloom-вспышка без появления (переиспользование). */
  | { kind: 'burst' }
  /** Фасадная схема: каскад панелей фронта модуля (panels — из ответа сервера). */
  | { kind: 'front-cascade'; moduleId: string; panels: number }
  /** Наполнение из флайаута (без 3D-границ ниши под курсором): мягкий вдох модуля. */
  | { kind: 'module-settle'; moduleId: string }
  /** Вставка предмета в нишу (дроп/кнопка палитры): scale-in от плоскости ниши
   *  + пульс границ. Нижу сцена находит сама по slotId из ДО-вставленного бандла. */
  | { kind: 'fill-item'; moduleId: string; slotId?: string | null }
  /** W4, таймлапс истории: модули, которых в следующем состоянии сцены нет,
   *  сжимаются к точке ухода ДО restore (farewell). Иды — из диффа compare. */
  | { kind: 'timelapse-farewell'; entityIds: string[] }
  /** W4, таймлапс истории: заявка на мягкое появление вернувшихся/добавленных
   *  модулей — армируется ДО restore, чтобы useModuleBirth не анимировал их
   *  вторым владельцем (claimedBirthIds). */
  | { kind: 'timelapse-greet'; entityIds: string[] };

export type PlacementEffectAnnouncement = PlacementEffectRequest & { modelId: string };

/** modelId фильтруется на сцене: null/undefined (нет активной модели) — молча
 *  пропускается, анонс без получателя не должен ронять мутацию. */
export function announcePlacementEffect(request: PlacementEffectRequest, modelId: string | null | undefined): void {
  if (typeof window === 'undefined' || !modelId) return;
  window.dispatchEvent(new CustomEvent<PlacementEffectAnnouncement>(PLACEMENT_EFFECT_EVENT, {
    detail: { ...request, modelId },
  }));
}

export function subscribePlacementEffects(
  handler: (request: PlacementEffectAnnouncement) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<PlacementEffectAnnouncement | undefined>).detail;
    if (!detail || !detail.modelId) return;
    handler(detail);
  };
  window.addEventListener(PLACEMENT_EFFECT_EVENT, listener);
  return () => window.removeEventListener(PLACEMENT_EFFECT_EVENT, listener);
}
